import { cloneElement, forwardRef, isValidElement, memo, useMemo, type ReactElement, type WheelEvent } from 'react';
import { useTheme } from 'styled-components';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';

import { Loader } from '../../../Elements/Loader/index.tsx';
import type { InputProps } from '../../common/index.ts';
import { StyledInput } from '../../common/StyledInput.ts';
import { StyledInputContainer } from '../../common/StyledInputContainer.ts';
import { IconContainer, InputWrapper, MaxLengthContainer } from './styled.ts';

interface IconProps {
  color?: string;
  onClick?: () => void;
}

export const Input = memo(
  forwardRef<HTMLInputElement, InputProps>(function Input(
    { value, isLoading, success, warning, error, icon: iconProp, isActionIcon, maxLength, dataTestId, ...props },
    ref,
  ) {
    const { colors } = useTheme();
    const inputDisabled = props.readOnly || props.disabled;

    const icon = useMemo(
      () =>
        isValidElement(iconProp)
          ? cloneElement(iconProp as ReactElement<IconProps>, {
              color: inputDisabled ? colors.grey30_1 : colors.grey100_1,
              onClick: undefined,
            })
          : iconProp,
      [colors.grey100_1, colors.grey30_1, iconProp, inputDisabled],
    );

    const handleIconClick = useMemo(
      () => (isValidElement(iconProp) && 'onClick' in iconProp.props ? iconProp.props.onClick : undefined),
      [iconProp],
    );

    const handleWheel = useCallbackRef((e: WheelEvent<HTMLInputElement>) => {
      if (props.type === 'number') {
        e.currentTarget.blur();
      }
    });

    return (
      <StyledInputContainer $inputDisabled={inputDisabled} $success={success} $warning={warning} $error={error} data-testid={dataTestId}>
        <InputWrapper>
          <StyledInput
            value={value}
            $withIcon={isLoading || (!!iconProp && !maxLength)}
            maxLength={maxLength}
            onWheel={handleWheel}
            ref={ref}
            {...props}
          />
          {isLoading && (
            <IconContainer>
              <Loader fill={colors.grey100_1} background='transparent' />
            </IconContainer>
          )}
          {iconProp && !isLoading && !maxLength && (
            <IconContainer $inputDisabled={inputDisabled} $isActionIcon={isActionIcon} onClick={handleIconClick}>
              {icon}
            </IconContainer>
          )}
        </InputWrapper>
        {maxLength && (
          <MaxLengthContainer data-testid='max-length-counter'>{`${value?.toString().length ?? 0}/${maxLength}`}</MaxLengthContainer>
        )}
      </StyledInputContainer>
    );
  }),
);

Input.displayName = 'Input';
